import {
  Tabs,
  TabList,
  Tab,
  TabPanel,
  TabPanels,
  Text,
  UseDisclosureReturn,
  ModalOverlay,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  Box,
} from "@chakra-ui/react";
import { JsonEditor } from "json-edit-react";
import React from "react";
import { JsonToTable } from "react-json-to-table";
import { colors } from "../../../../../theme/foundations/colors";
import { AppModal } from "../../../../modals/AppModal";
import { AppModalContent } from "../../../../modals/AppModalContent";
import { AppModalFooter } from "../../../../modals/AppModalFooter";

export function SourceDataViewerModal({
  sourceData,
  disclosure,
}: {
  sourceData: unknown[];
  disclosure: UseDisclosureReturn;
}): React.ReactElement {
  return (
    <AppModal
      isOpen={disclosure.isOpen}
      onClose={disclosure.onClose}
      closeOnEsc={true}
      closeOnOverlayClick={true}
      size="3xl"
    >
      <ModalOverlay />

      <AppModalContent>
        <ModalHeader>Source data</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Text color="gray.600" mb={6}>
            The original source information this transaction was imported from.
          </Text>
          {/* source data viewer can be expensive to render, so only do it once needed */}
          {disclosure.isOpen && <SourceDataViewer sourceData={sourceData} />}
        </ModalBody>

        <AppModalFooter>
          <Button
            type="button"
            variant="outline"
            colorScheme="gray"
            onClick={disclosure.onClose}
          >
            Close
          </Button>
        </AppModalFooter>
      </AppModalContent>
    </AppModal>
  );
}

function SourceDataViewer({ sourceData }: { sourceData: unknown[] }) {
  return (
    <Tabs isLazy width="full">
      <TabList>
        <Tab>Table</Tab>
        <Tab>Object</Tab>
      </TabList>

      <TabPanels>
        <TabPanel px={0} overflow="auto">
          <SourceDataTable sourceData={sourceData} />
        </TabPanel>
        <TabPanel px={0}>
          <SourceDataObject sourceData={sourceData} />
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
}

function SourceDataTable({ sourceData }: { sourceData: unknown[] }) {
  return (
    <Box
      sx={{
        ".json-to-table *": {
          fontFamily: "Roboto Mono, monospace",
          fontSize: "sm",
        },
        ".json-to-table": {
          width: "100%",
        },
        ".json-to-table tr:first-of-type td": {
          fontWeight: "bold",
        },
        ".json-to-table td": {
          verticalAlign: "top",
        },
      }}
    >
      <JsonToTable json={sourceData} />
    </Box>
  );
}

function SourceDataObject({ sourceData }: { sourceData: unknown[] }) {
  return (
    <Box
      maxW="full"
      sx={{
        ".jer-editor-container": {
          fontSize: "sm",
        },

        // try and get long string values to wrap a bit nicer
        ".jer-value-string": {
          whiteSpace: "normal",
          overflowWrap: "anywhere",
          wordBreak: "break-all",
        },
      }}
    >
      <JsonEditor
        theme={{
          styles: {
            container: {
              fontFamily: "Roboto Mono, monospace",
            },
            property: {
              color: colors.gray[800],
              fontWeight: 700,
            },
            string: {
              color: colors.gray[700],
            },
            number: {
              color: colors.gray[700],
            },
            boolean: {
              color: colors.gray[700],
            },
            null: {
              color: colors.gray[500],
            },
          },
        }}
        restrictAdd
        restrictDelete
        restrictEdit
        restrictDrag
        showArrayIndices={false}
        showCollectionCount={false}
        rootName=""
        maxWidth="full"
        data={sourceData}
      />
    </Box>
  );
}
